<template>
  <div class="text-puzzle-preview">
    <div v-if="selectedElement" class="card card-preview">
      <cardHeader :elementLabel="elementLabel" :elementType="selectedElement.el_Type" :mode="'Preview'" />
      <div class="card-body">
        <!-- Main prompt -->
        <div class="mb-2">
          <h3>{{ selectedElement.el_Text }}</h3>
        </div>

        <!-- Rich text description -->
        <div v-if="selectedElement.el_RichText" class="rich-text-content mb-3" v-html="selectedElement.el_RichText"></div>


        <!-- Text with gaps -->
        <div class="puzzle-text-container mb-4">
          <div 
            class="puzzle-text" 
            v-html="getTextWithGaps()"
            @drop="handleDrop"
            @dragover="handleDragOver"
          ></div>
        </div>

        <!-- Draggable elements -->
        <div class="drag-elements-container" v-if="shuffledElements.length > 0">
          <h6 class="mb-3">
            <i class="fas fa-hand-point-right me-2"></i>
            Drag these pieces to fill the gaps:
          </h6>
          <div class="drag-elements">
            <div
              v-for="element in shuffledElements"
              :key="element.id"
              class="drag-element"
              :class="{ 'placed': placedElements.includes(element.id) }"
              draggable="true"
              @dragstart="handleDragStart($event, element)"
            >
              {{ element.text }}
            </div>
          </div>
        </div>


        <!-- Report Container -->
        <div class="ReportContainer" v-if="showReport">
          <div class="row">
            <div class="col">
              <h5>Preview Results:</h5>
            </div>
            <div class="col" style="text-align: right;"><strong>Date:</strong> {{ currentDate }}</div>
          </div>
          
          <hr>
          
          <!-- Gap-by-gap results -->
          <div class="mb-3">
            <strong>Gap Results:</strong>
            <div v-for="(gap, index) in interactionData.gaps" :key="gap.id" class="gap-result-item">
              <span class="gap-number">Gap {{ index + 1 }}:</span>
              <span class="gap-text">"{{ gap.text }}"</span>
              <span class="gap-status">
                <span v-if="gapContents[gap.id] === gap.text" class="success">✅ Correct</span>
                <span v-else-if="gapContents[gap.id]" class="error">❌ Incorrect ({{ gapContents[gap.id] }})</span>
                <span v-else class="missing">⭕ Not filled</span>
              </span>
            </div>
          </div>
          
          <!-- Summary -->
          <p><strong>Score:</strong> {{ results.score }}</p>
          <p><strong>Max-Score:</strong> {{ results.maxScore }}</p>
          <p><strong>Penalties:</strong> {{ results.penalties }}</p>
          <p><strong>Completion Rate:</strong> {{ results.completionRate }}%</p>
          <p><strong>Correct Gaps:</strong> {{ results.correctGaps }} / {{ interactionData.gaps.length }}</p>
        </div>

        <!-- Preview info -->
        <div  class="preview-info mt-4 p-3">
          <!-- Preview Controls -->
          <div class="preview-controls mt-3">
            <button class="btn btn-primary btn-sm me-2" @click="showReportOnSubmit">
              <i class="fas fa-check me-1"></i>Submit
            </button>
            <button v-if="showReport" class="btn btn-console btn-sm me-2" @click="closeReport">
              <i class="fas fa-times me-1"></i>Close Report
            </button>
            <button class="btn btn-secondary btn-sm" @click="resetPreview">
              <i class="fas fa-redo me-1"></i>Reset
            </button>
          </div>
        </div>
      </div>
    </div>
    <div v-else class="alert alert-info">
      Select an element to preview
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import cardHeader from '../default/cardHeader.vue'
import { useTestStore } from '../../../stores/testStore.js'

const store = useTestStore()
const placedElements = ref([])
const gapContents = ref({})
const draggedElement = ref(null)

// Report functionality
const showReport = ref(false)
const results = ref({
  score: 0,
  maxScore: 0,
  penalties: 0,
  completionRate: 0,
  correctGaps: 0
})

const selectedElement = computed(() => store.selectedElement)

// Helper computed for interaction data with defaults
const interactionData = computed(() => {
  const element = selectedElement.value
  if (!element) return {
    el_SourceText: '',
    gaps: [],
    dragElements: [],
    distractors: [],
    pointsPerCorrect: 1,
    penaltyPerIncorrect: 0
  }

  const defaults = {
    el_SourceText: '',
    gaps: [],
    dragElements: [],
    distractors: [],
    pointsPerCorrect: 1,
    penaltyPerIncorrect: 0
  }

  return { ...defaults, ...(element.el_InteractionData || {}) }
})

// Synchronize label
const elementLabel = ref('')
watch(() => store.selectedElementLabel, (newLabel) => {
  elementLabel.value = newLabel || ''
}, { immediate: true })

const shuffledElements = computed(() => {
  const elements = [...(interactionData.value.dragElements || [])]
  // Always shuffle elements for better user experience
  for (let i = elements.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[elements[i], elements[j]] = [elements[j], elements[i]]
  }
  return elements
})

const getTextWithGaps = () => {
  if (!interactionData.value.el_SourceText) {
    return '<p>No source text available. Please add content in the editor.</p>'
  }

  let content = interactionData.value.el_SourceText
  const gaps = interactionData.value.gaps || []

  // Replace gap placeholders with actual gap elements
  gaps.forEach((gap, index) => {
    const gapNumber = index + 1
    const gapPlaceholder = `[GAP_${gapNumber}]`
    const gapContent = gapContents.value[gap.id] || ''
    
    const gapElement = `
      <span 
        class="text-gap ${gapContent ? 'filled' : 'empty'}" 
        data-gap-id="${gap.id}"
        data-gap-index="${index}"
      >
        ${gapContent || `[Drop here ${gapNumber}]`}
      </span>
    `
    
    content = content.replace(gapPlaceholder, gapElement)
  })

  return content
}

const handleDragStart = (event, element) => {
  draggedElement.value = element
  event.dataTransfer.setData('text/plain', element.id)
  event.dataTransfer.effectAllowed = 'move'
}

const handleDragOver = (event) => {
  event.preventDefault()
  event.dataTransfer.dropEffect = 'move'
  
  // Highlight drop zones
  const target = event.target
  if (target.classList.contains('text-gap') && target.classList.contains('empty')) {
    target.classList.add('drag-over')
  }
}

const handleDrop = (event) => {
  event.preventDefault()
  const target = event.target

  // Remove drag-over highlighting
  document.querySelectorAll('.text-gap').forEach(gap => {
    gap.classList.remove('drag-over')
  })

  if (target.classList.contains('text-gap')) {
    const gapId = target.getAttribute('data-gap-id')
    const elementId = event.dataTransfer.getData('text/plain')
    const element = (interactionData.value.dragElements || []).find(el => el.id === elementId)

    if (element && gapId) {
      // Always allow repositioning for better user experience
      Object.keys(gapContents.value).forEach(key => {
        if (gapContents.value[key] === element.text) {
          delete gapContents.value[key]
        }
      })
      placedElements.value = placedElements.value.filter(id => id !== elementId)

      // Place element in new gap
      gapContents.value[gapId] = element.text
      if (!placedElements.value.includes(elementId)) {
        placedElements.value.push(elementId)
      }

      // Always show visual feedback for better user experience
      target.classList.add('drop-success')
      setTimeout(() => {
        target.classList.remove('drop-success')
      }, 500)
    }
  }
}

// Date for report
const currentDate = computed(() => {
  const now = new Date()
  return now.toLocaleString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  })
})

// Calculate detailed results
const calculateResults = () => {
  const gaps = interactionData.value.gaps || []
  const pointsPerCorrect = interactionData.value.pointsPerCorrect || 1
  const penaltyPerIncorrect = interactionData.value.penaltyPerIncorrect || 0
  
  let score = 0
  let penalties = 0
  let correctGaps = 0
  
  gaps.forEach(gap => {
    const userAnswer = gapContents.value[gap.id]
    if (userAnswer) {
      if (userAnswer === gap.text) {
        score += pointsPerCorrect
        correctGaps++
      } else {
        penalties += penaltyPerIncorrect
      }
    }
  })

  const maxScore = gaps.length * pointsPerCorrect
  const completionRate = gaps.length > 0 ? (Object.keys(gapContents.value).length / gaps.length) * 100 : 100

  results.value = {
    score: Math.max(0, score - penalties),
    maxScore,
    penalties,
    completionRate: Math.round(completionRate),
    correctGaps
  }
}

// Submit answers and show report
const showReportOnSubmit = () => {
  calculateResults()
  showReport.value = true
}

// Close report
const closeReport = () => {
  showReport.value = false
}

// Reset preview
const resetPreview = () => {
  placedElements.value = []
  gapContents.value = {}
  showReport.value = false
}

// Reset preview when element changes
watch(selectedElement, () => {
  placedElements.value = []
  gapContents.value = {}
  showReport.value = false
}, { deep: true })

onMounted(() => {
  // Add event listeners to remove drag-over highlighting
  document.addEventListener('dragend', () => {
    document.querySelectorAll('.text-gap').forEach(gap => {
      gap.classList.remove('drag-over')
    })
  })
})
</script>

<style scoped>
.text-puzzle-preview {
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}

.card-preview {
  border: 1px solid #dee2e6;
  border-radius: 8px;
}

.rich-text-content {
  color: #6c757d;
  font-size: 0.95em;
}

.puzzle-text-container {
  background-color: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
  border: 2px dashed #dee2e6;
}

.puzzle-text {
  font-size: 1.1em;
  line-height: 1.6;
}

.drag-elements {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  padding: 15px;
  background-color: #e9ecef;
  border-radius: 8px;
  min-height: 60px;
}

.drag-element {
  background-color: #007bff;
  color: white;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: grab;
  user-select: none;
  font-weight: 500;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.drag-element:hover {
  background-color: #0056b3;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.15);
}

.drag-element:active {
  cursor: grabbing;
  transform: scale(0.95);
}

.drag-element.placed {
  opacity: 0.5;
  background-color: #6c757d;
  cursor: not-allowed;
}

:deep(.text-gap) {
  display: inline-block;
  min-width: 100px;
  min-height: 24px;
  padding: 4px 8px;
  margin: 0 2px;
  border: 2px dashed #007bff;
  border-radius: 4px;
  background-color: white;
  color: #007bff;
  font-weight: 500;
  text-align: center;
  vertical-align: baseline;
  transition: all 0.2s ease;
}

:deep(.text-gap.empty) {
  color: #6c757d;
  font-style: italic;
  font-size: 0.9em;
}

:deep(.text-gap.filled) {
  background-color: #d4edda;
  border-color: #28a745;
  color: #155724;
}

:deep(.text-gap.drag-over) {
  background-color: #cce5ff;
  border-color: #0066cc;
  transform: scale(1.05);
}

:deep(.text-gap.drop-success) {
  animation: dropSuccess 0.5s ease;
}

@keyframes dropSuccess {
  0% { background-color: #d4edda; }
  50% { background-color: #90ee90; }
  100% { background-color: #d4edda; }
}


.preview-controls {
  text-align: right;
  padding-top: 15px;
}

.btn-console {
  background-color: rgb(78, 54, 54);
  color: white;
  border: none;
}

.btn-console:hover {
  background-color: rgb(119, 104, 104);
  color: white;
}

/* Report Container Styles */
.ReportContainer {
  border: grey 1px solid;
  border-radius: 3px;
  padding: 20px;
  background-color: rgb(54, 63, 67);
  color: greenyellow;
  margin-top: 20px;
}

.ReportContainer h5 {
  color: greenyellow;
  margin-bottom: 15px;
}

.ReportContainer hr {
  border-color: #6c757d;
}

.ReportContainer p {
  margin-bottom: 8px;
  font-family: 'Courier New', monospace;
}

.gap-result-item {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  padding: 5px;
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 4px;
  font-family: 'Courier New', monospace;
}

.gap-number {
  min-width: 80px;
  font-weight: bold;
  color: #ffd700;
}

.gap-text {
  flex: 1;
  margin: 0 10px;
  font-style: italic;
  color: #87ceeb;
}

.gap-status {
  min-width: 150px;
  text-align: right;
}

.gap-status .success {
  color: #28a745;
  font-weight: bold;
}

.gap-status .error {
  color: #dc3545;
  font-weight: bold;
}

.gap-status .missing {
  color: #ffc107;
  font-weight: bold;
}

</style>